import { Button, List, CircularProgress } from "@mui/material";
import { useState, useContext, useCallback } from "react";

import { useDebouncedEffect } from "../../../../../../../hooks/useDebounceEffect";
import axios from "../../../../../../api/axios";
import { GenerateQueryParameterString } from "../../../../../../global/GenerateQueryParamString";
import OrderingArrow from "../../../../../../global/OrderingArrow";
import PaginationControls from "../../../../../../global/PaginationControls";
import HNSColors from "../../../../../../ui library/HNSColors";

import Search from "./components/Search";
import { ReportsListDispatchContext, ReportsListStateContext } from "./context/reportsListProvider";
import ReportsListItem from "./ReportsListItem";
import { setSearch, setOrderingVal, setCurrentPage, setOffset } from "./state/actions";

const columnHeaderTypography = {
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0",
  color: HNSColors.neutrals[100],
};

const ReportsListWithState = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resultCount, setResultCount] = useState({ total: "", current: "" });
  const [responseData, setResponseData] = useState([]);
  const [trigger, setTrigger] = useState(false);
  const state = useContext(ReportsListStateContext);
  const dispatch = useContext(ReportsListDispatchContext);

  const updateSearch = (searchStr) => {
    setSearch(dispatch, searchStr);
  };

  const getFilterParamsList = useCallback(() => {
    const paramObj = {
      ...(state.search ? { search: state.search } : {}),
      ...(state.resLimit ? { limit: state.resLimit } : {}),
      ...(state.orderingVal ? { ordering: state.orderingVal } : {}),
      ...(state.offset ? { offset: state.offset } : {}),
    };
    return GenerateQueryParameterString(paramObj);
  }, [state.search, state.resLimit, state.orderingVal, state.offset]);

  const sendRequest = () => {
    axios
      .get(`/api/tabular_reports/${getFilterParamsList()}`, {})
      .then(function (response) {
        setResponseData(response.data.results);
        setResultCount({
          total: response.data.count,
          current: response.data.results.length,
        });
        setLoading(false);
      })
      .catch(function (err) {
        setError(err);
        setLoading(false);
      });
  };

  const handleReload = () => {
    setError("");
    setTrigger(!trigger);
  };

  const handleChange = (event, value) => {
    setCurrentPage(dispatch, value);
    setOffset(dispatch, state.resLimit * (value - 1));
  };

  const resetPagination = () => {
    setOffset(dispatch, null);
    setCurrentPage(dispatch, 1);
  };

  useDebouncedEffect(
    () => {
      sendRequest();
    },
    [state.search, state.orderingVal, state.offset, trigger],
    250
  );

  const setOrderValue = (orderVal) => {
    setOrderingVal(dispatch, orderVal);
  };

  return (
    <div id="Reports" data-testid="Reports" className="bg-white px-10 py-5">
      <div className="mb-6 flex h-full w-full flex-nowrap items-center overflow-auto sm:mx-auto">
        <h1
          className="z-10 flex items-center py-4 text-left text-xl font-semibold leading-[100%] tracking-[0.00em]"
          style={{ color: HNSColors.primary[500] }}
        >
          Reports
        </h1>
      </div>

      <Search setSearchState={updateSearch} resetPagination={resetPagination} />

      <div className="mt-6">
        <div className="flex w-full flex-row items-center gap-2 py-4">
          <div className="mr-2 flex w-[25%] items-center gap-2">
            <h2 className="ml-4 md:block" style={columnHeaderTypography}>
              Name
            </h2>
            {responseData && responseData.length > 0 && (
              <OrderingArrow
                val={"display_name"}
                orderingVal={state.orderingVal}
                setOrderingVal={setOrderValue}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <h2 className="md:block" style={columnHeaderTypography}>
              Description
            </h2>
          </div>
        </div>
        <div>
          <List
            className="flex max-h-[50vh] min-h-[40vh] w-full flex-col overflow-y-auto pb-2"
            sx={{
              p: 0,
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: HNSColors.primary[500],
                borderRadius: "999px",
              },
            }}
          >
            {loading ? (
              <div key="loading-indicator" className="my-24 flex justify-center">
                <CircularProgress
                  sx={{
                    color: HNSColors.primary[500],
                  }}
                />
              </div>
            ) : !error ? (
              <>
                {!responseData || responseData.length === 0 ? (
                  <p className="text-center">No Reports Available</p>
                ) : (
                  responseData.map((item, index) => (
                    <ReportsListItem key={item.pk ?? index} report={item} rowIndex={index} />
                  ))
                )}
              </>
            ) : (
              <>
                <p className="text-center">An error occurred. Please try again.</p>
                <div className="flex w-full justify-center">
                  <Button className="w-fit rounded px-4 py-2" onClick={handleReload}>
                    Try again
                  </Button>
                </div>
              </>
            )}
          </List>
          {responseData && responseData.length > 0 && (
            <div className="flex w-full justify-center px-0 py-2 md:px-0">
              <PaginationControls
                resultCount={resultCount}
                handleChange={handleChange}
                currentPage={state.currentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default ReportsListWithState;
